import { useAnalytics } from "@codat/analytics";
import {
  Button,
  Modal,
  ModalBody,
  SelectInput,
  useToast,
} from "@codat/orchard-ui";
import { SelectInputValue } from "@codat/orchard-ui/dist/es/components/SelectInput";
import { AxiosError } from "axios";
import React, { useRef, useState } from "react";
import { ApiError } from "shared";

import TextInput from "components/TextInput";

import { useUserManagementService } from "../UserManagementService";
import styles from "./AddUserModal.module.css";

interface AddUserModalProps {
  open: boolean;
  userRoleOptions: string[];
  onSuccess: () => void;
  onClose: () => void;
}
const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  userRoleOptions,
  onSuccess,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [clientRole, setClientRole] = useState("");
  const [hasAttemptedCreate, setHasAttemptedCreate] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isRoleValid, setIsRoleValid] = useState(false);
  const [roleError, setRoleError] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const service = useUserManagementService()?.inviteUser;

  if (!service) {
    throw new Error("No User Management Service provider");
  }

  const resetModal = () => {
    setEmail("");
    setClientRole("");
    setHasAttemptedCreate(false);
    setIsEmailValid(false);
    setEmailError("");
    setIsRoleValid(false);
    setRoleError("");
  };

  const inviteUser = () => {
    sendEvent("Send Invitation Button - Click");
    const emailValid = validateEmail(email);
    const clientRoleValid = validateRole(clientRole);
    if (!emailValid || !clientRoleValid) {
      setHasAttemptedCreate(true);
      return;
    }

    service
      .inviteUser(email, clientRole)
      .then(() => {
        addToast({
          message: `User invited to client`,
          variant: "success",
        });
        onSuccess();
        resetModal();
      })
      .catch((error: AxiosError<ApiError>) => {
        const errorMessage = error.response?.data?.error ?? "Unknown error";
        addToast({
          message: `Unable to create user: ${errorMessage}`,
          variant: "error",
        });
      });
  };

  const setUserEmail: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (hasAttemptedCreate) {
      validateEmail(event.target.value);
    }

    setEmail(event.target.value);
  };

  const setUserRole = (selectInput: SelectInputValue | null | undefined) => {
    sendEvent("Role - Change", { selected: selectInput?.value });
    if (selectInput) {
      if (hasAttemptedCreate) {
        validateRole(selectInput.value);
      }
      setClientRole(selectInput.value);
    }
  };

  function validateEmail(input: string) {
    let isValid = false;
    const reg =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!input) {
      setEmailError("Please enter a valid email");
    } else if (input.length < 3) {
      setEmailError("Minimum length is 3 chars");
    } else if (input.length > 256) {
      setEmailError("Maximum length is 256 chars");
    } else if (reg.test(input) === false) {
      setEmailError("Email is invalid");
    } else {
      isValid = true;
      setEmailError("");
    }

    setIsEmailValid(isValid);
    return isValid;
  }

  function validateRole(input: string) {
    let isValid = false;
    if (!input) {
      setRoleError("Please select a role");
    } else {
      isValid = true;
      setRoleError("");
    }

    setIsRoleValid(isValid);
    return isValid;
  }

  return (
    <Modal
      onClose={() => {
        resetModal();
        onClose();
        sendEvent("Close");
      }}
    >
      {open && (
        <ModalBody
          title="New user"
          size="medium"
          subtitle="The user will receive an invite via email."
          subtitleClassName={styles.subtitle}
          className={styles.card}
          data-testid="add-user-modal"
          onClose={() => {
            resetModal();
            onClose();
            sendEvent("Close Button - Click");
          }}
        >
          <div className={styles.inputContainer}>
            <TextInput
              inputName="Email Input"
              error={hasAttemptedCreate && !isEmailValid}
              errorMessage={emailError}
              id="user-email-input"
              data-testid="user-email-input"
              onChange={setUserEmail}
              value={email}
              label="Email"
              ref={inputRef}
              data-autofocus
            />
            <SelectInput
              error={hasAttemptedCreate && !isRoleValid}
              errorMessage={roleError}
              id="user-role-input"
              data-testid="user-role-input"
              label="Role"
              showLabel
              placeholder="Select"
              handleSelectedItemChange={setUserRole}
              items={userRoleOptions.map((x) => ({ label: x, value: x }))}
            />
          </div>
          <div className={styles.footer}>
            <Button
              data-testid="send-invitation-button"
              className={styles.submitButton}
              label="Send invitation"
              onClick={inviteUser}
              isLoading={service.isLoading()}
            />
          </div>
        </ModalBody>
      )}
    </Modal>
  );
};

export default AddUserModal;
